import { getPortalConfiguration, PORTAL_TYPES, PRODUCTION_PORTAL_HOSTS } from "./portalConfig.js";
import { clearPortalSession, getPortalSession, getPortalSessionOwner, setPortalSession } from "./portalSession.js";

export { PORTAL_TYPES, PRODUCTION_PORTAL_HOSTS } from "./portalConfig.js";

export { PERSONA_STORAGE_KEY } from "./portalSession.js";

const PORTAL_LABELS = {
  grower: "Grower",
  partner: "Partner",
  aggregator: "Aggregator",
  admin: "Admin",
};

const PORTAL_DEFAULT_PATHS = {
  grower: "/dashboard",
  partner: "/dashboard",
  aggregator: "/dashboard",
  admin: "/dashboard",
};

const PORTAL_ROUTE_PATTERNS = {
  grower: [
    /^\/$/,
    /^\/dashboard$/,
    /^\/profile$/,
    /^\/support$/,
    /^\/utilities(?:\/.*)?$/,
    /^\/equipment(?:\/.*)?$/,
    /^\/fertilizers(?:\/.*)?$/,
    /^\/excel$/,
    /^\/projects(?:\/.*)?$/,
    /^\/addfarm$/,
    /^\/farm\/form$/,
    /^\/farm\/[^/]+$/,
    /^\/farm\/[^/]+\/editfarm$/,
    /^\/farm\/[^/]+\/form$/,
    /^\/farm\/[^/]+\/addfield$/,
    /^\/farm\/[^/]+\/field\/form$/,
    /^\/farm\/[^/]+\/field\/[^/]+$/,
    /^\/farm\/[^/]+\/field\/[^/]+\/editfield$/,
    /^\/farm\/[^/]+\/field\/[^/]+\/form$/,
    /^\/farm\/[^/]+\/field\/[^/]+\/cropyear$/,
    /^\/farm\/[^/]+\/field\/[^/]+\/addcropyear$/,
    /^\/farm\/[^/]+\/field\/[^/]+\/cropyear\/[^/]+$/,
    /^\/farm\/[^/]+\/field\/[^/]+\/cropyear\/[^/]+\/editcropyear$/,
    /^\/farm\/[^/]+\/field\/[^/]+\/cropyear\/[^/]+\/rotation$/,
    /^\/farm\/[^/]+\/field\/[^/]+\/cropyear\/[^/]+\/field-operations$/,
    /^\/farm\/[^/]+\/field\/[^/]+\/cropyear\/[^/]+\/field-operations\/cultivation$/,
    /^\/farm\/[^/]+\/field\/[^/]+\/cropyear\/[^/]+\/harvest$/,
    /^\/farm\/[^/]+\/field\/[^/]+\/cropyear\/[^/]+\/addanalysis$/,
    /^\/farm\/[^/]+\/field\/[^/]+\/cropyear\/[^/]+\/analysis$/,
  ],
  partner: [/^\/support$/, /^\/$/, /^\/dashboard$/, /^\/profile$/, /^\/projects(?:\/.*)?$/],
  aggregator: [
    /^\/$/,
    /^\/dashboard$/,
    /^\/profile$/,
    /^\/support$/,
    /^\/growers(?:\/.*)?$/,
    /^\/delegations(?:\/.*)?$/,
    /^\/imports(?:\/.*)?$/,
    /^\/excel(?:\/.*)?$/,
    /^\/reconciliation(?:\/.*)?$/,
    /^\/farm\/[^/]+$/,
    /^\/farm\/[^/]+\/form$/,
    /^\/farm\/[^/]+\/field\/form$/,
    /^\/farm\/[^/]+\/field\/[^/]+$/,
    /^\/farm\/[^/]+\/field\/[^/]+\/form$/,
    /^\/farm\/[^/]+\/field\/[^/]+\/cropyear$/,
    /^\/farm\/[^/]+\/field\/[^/]+\/addcropyear$/,
    /^\/farm\/[^/]+\/field\/[^/]+\/cropyear\/[^/]+$/,
    /^\/farm\/[^/]+\/field\/[^/]+\/cropyear\/[^/]+\/editcropyear$/,
    /^\/farm\/[^/]+\/field\/[^/]+\/cropyear\/[^/]+\/rotation$/,
    /^\/farm\/[^/]+\/field\/[^/]+\/cropyear\/[^/]+\/field-operations$/,
    /^\/farm\/[^/]+\/field\/[^/]+\/cropyear\/[^/]+\/field-operations\/cultivation$/,
    /^\/farm\/[^/]+\/field\/[^/]+\/cropyear\/[^/]+\/harvest$/,
    /^\/farm\/[^/]+\/field\/[^/]+\/cropyear\/[^/]+\/addanalysis$/,
    /^\/farm\/[^/]+\/field\/[^/]+\/cropyear\/[^/]+\/analysis$/,
  ],
  admin: [/^\/support$/, /^\/$/, /^\/dashboard$/, /^\/profile$/, /^\/persona(?:\/.*)?$/, /^\/workforce(?:\/.*)?$/, /^\/project-enrollments$/],
};

function isLocalhostLike(hostname = "") {
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname.endsWith(".localhost")
  );
}

function resolveLocalPortal(hostname = "") {
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return "grower";
  }

  if (hostname.endsWith(".localhost")) {
    const [subdomain] = hostname.split(".");
    if (subdomain === "partners") {
      return "partner";
    }
    if (subdomain === "aggregators") {
      return "aggregator";
    }
    if (subdomain === "admin") {
      return "admin";
    }
  }

  return "grower";
}

export function getPortalLabel(portal) {
  return PORTAL_LABELS[portal] || PORTAL_LABELS.grower;
}

export function getPortalDefaultPath(portal) {
  return PORTAL_DEFAULT_PATHS[portal] || PORTAL_DEFAULT_PATHS.grower;
}

export function resolvePortalHost(hostname = window.location.hostname, env = process.env) {
  const normalizedHost = (hostname || "").toLowerCase();
  const config = getPortalConfiguration(env);

  if (config.portal) {
    return {
      portal: config.portal,
      hostname: normalizedHost,
      environment: isLocalhostLike(normalizedHost) ? "local" : "production",
    };
  }

  const configuredPortal = PORTAL_TYPES.find(
    (portal) => new URL(config.origins[portal]).hostname === normalizedHost
  );
  if (configuredPortal) {
    return { portal: configuredPortal, hostname: normalizedHost, environment: "production" };
  }

  if (normalizedHost === PRODUCTION_PORTAL_HOSTS.partner) {
    return { portal: "partner", hostname: normalizedHost, environment: "production" };
  }

  if (normalizedHost === PRODUCTION_PORTAL_HOSTS.aggregator) {
    return { portal: "aggregator", hostname: normalizedHost, environment: "production" };
  }

  if (normalizedHost === PRODUCTION_PORTAL_HOSTS.admin) {
    return { portal: "admin", hostname: normalizedHost, environment: "production" };
  }

  if (
    normalizedHost === PRODUCTION_PORTAL_HOSTS.grower ||
    isLocalhostLike(normalizedHost)
  ) {
    return {
      portal: isLocalhostLike(normalizedHost)
        ? resolveLocalPortal(normalizedHost)
        : "grower",
      hostname: normalizedHost,
      environment: isLocalhostLike(normalizedHost) ? "local" : "production",
    };
  }

  return { portal: "grower", hostname: normalizedHost, environment: "unknown" };
}

export function readStoredPersona() {
  // Keep existing callers compatible without persisting the simulated portal.
  return getPortalSession().personaPortal;
}

export function writeStoredPersona(portal, account) {
  return setPortalSession({ portal, personaPortal: portal, owner: getPortalSessionOwner(account) }) ? portal : null;
}

export function clearStoredPersona() {
  if (getPortalSession().personaPortal) clearPortalSession();
  return null;
}

export function getSwitchablePersonas(viewer) {
  return (viewer?.allowedPortals || []).filter(portal => PORTAL_TYPES.includes(portal));
}

export function getDesiredPortal(viewer, personaPortal, hostPortal = null) {
  if (!viewer) {
    return hostPortal || "grower";
  }

  if (
    personaPortal &&
    getSwitchablePersonas(viewer).includes(personaPortal)
  ) {
    return personaPortal;
  }

  if (
    hostPortal &&
    getSwitchablePersonas(viewer).includes(hostPortal)
  ) {
    return hostPortal;
  }

  return viewer.activePortal || viewer.defaultPortal || "grower";
}

export function pathBelongsToPortal(portal, pathname = "/") {
  const patterns = PORTAL_ROUTE_PATTERNS[portal] || [];
  return patterns.some((pattern) => pattern.test(pathname));
}

export function buildPortalUrl(
  portal,
  {
    pathname = getPortalDefaultPath(portal),
    search = "",
    hash = "",
    protocol = window.location.protocol,
    hostname = window.location.hostname,
    port = window.location.port,
    env = process.env,
  } = {}
) {
  const currentHost = (hostname || "").toLowerCase();
  const config = getPortalConfiguration(env);
  const targetPortal = PORTAL_TYPES.includes(portal) ? portal : "grower";
  let targetOrigin = config.origins[targetPortal];

  if (isLocalhostLike(currentHost)) {
    let targetHostname;
    if (portal === "grower") {
      targetHostname = currentHost === "127.0.0.1" ? "127.0.0.1" : "localhost";
    } else {
      targetHostname = `${portal === "partner" ? "partners" : portal === "aggregator" ? "aggregators" : "admin"}.localhost`;
    }
    targetOrigin = `${protocol}//${targetHostname}${port ? `:${port}` : ""}`;
  } else if (config.portal === targetPortal) {
    // The installed portal stays on its own origin, including static previews.
    targetOrigin = `${protocol}//${currentHost}${port ? `:${port}` : ""}`;
  }

  const normalizedPath = pathname || getPortalDefaultPath(portal);
  const withSlash = normalizedPath.startsWith("/") ? normalizedPath : `/${normalizedPath}`;
  return `${targetOrigin}${withSlash}${search}${hash}`;
}

export function resolvePortalRedirect({
  viewer,
  hostPortal,
  pathname = "/",
  search = "",
  hash = "",
  hostname = window.location.hostname,
  protocol = window.location.protocol,
  port = window.location.port,
  personaPortal = null,
  env = process.env,
}) {
  // An explicit authorized selection is an in-tab simulation, never a host hop.
  if (personaPortal && getSwitchablePersonas(viewer).includes(personaPortal)) {
    return null;
  }
  const desiredPortal = getDesiredPortal(viewer, personaPortal, hostPortal);

  if (desiredPortal === hostPortal) {
    return null;
  }

  const nextPath = pathBelongsToPortal(desiredPortal, pathname)
    ? pathname
    : getPortalDefaultPath(desiredPortal);

  return {
    portal: desiredPortal,
    url: buildPortalUrl(desiredPortal, {
      pathname: nextPath,
      search,
      hash,
      hostname,
      protocol,
      port,
      env,
    }),
  };
}
